import { useAuthStore } from '@/stores/useAuthStore';
import useContactStore from '@/stores/useContactStore';
import { Contact } from '@/types/contact';
import { formatDateTime } from '@/utils/formatDateTime';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';

export interface ExportField {
  field: keyof Contact | 'created_at_formatted';
  label: string;
  checked: boolean;
}

export const useContactActions = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { deleteContact } = useContactStore();
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [contactsToExport, setContactsToExport] = useState<Contact[]>([]);
  const [exportFields, setExportFields] = useState<ExportField[]>([]);

  // Initialiser les champs d'exportation
  const initExportFields = useCallback(() => {
    setExportFields([
      { field: 'full_name', label: t('common:fields.fullname'), checked: true },
      { field: 'email', label: t('common:fields.email'), checked: true },
      { field: 'phone', label: t('common:fields.phone'), checked: true },
      { field: 'company', label: t('common:fields.company'), checked: true },
      { field: 'notes', label: t('common:fields.notes'), checked: true },
      {
        field: 'created_at_formatted',
        label: t('common:fields.created_at'),
        checked: true,
      },
    ]);
  }, [t]);

  // Initialiser les champs d'exportation au chargement du hook
  useEffect(() => {
    initExportFields();
  }, [initExportFields]);

  // Gérer la suppression d'un contact
  const handleDeleteContact = async (contact: Contact) => {
    if (user?.id) {
      try {
        await deleteContact(user.id, contact.id);
        toast.success(t('listContacts:delete.success'));
        return true;
      } catch (error) {
        toast.error(t('listContacts:delete.error'));
        return false;
      }
    }
    return false;
  };

  // Gérer la suppression en masse
  const handleBulkDelete = async (contacts: Contact[]) => {
    if (contacts.length > 0 && user?.id) {
      try {
        const deletePromises = contacts.map(contact =>
          deleteContact(user.id, contact.id),
        );

        await Promise.all(deletePromises);
        toast.success(
          t('listContacts:bulkDelete.success', { count: contacts.length }),
        );
        return true;
      } catch (error) {
        toast.error(t('listContacts:bulkDelete.error'));
        return false;
      }
    }
    return false;
  };

  // Préparer pour l'exportation
  const prepareForExport = (contacts: Contact[]) => {
    // S'assurer que les champs d'exportation sont initialisés
    if (exportFields.length === 0) {
      initExportFields();
    }
    setContactsToExport(contacts);
  };

  // Gérer le changement de champ pour l'exportation
  const handleExportFieldChange = (field: ExportField) => {
    setExportFields(
      exportFields.map(f =>
        f.field === field.field ? { ...f, checked: !f.checked } : f,
      ),
    );
  };

  // Exporter les contacts
  const exportContacts = () => {
    if (contactsToExport.length === 0) {
      toast.error(t('listContacts:export.error.noContacts'));
      return false;
    }

    if (exportFields.filter(f => f.checked).length === 0) {
      toast.error(t('listContacts:export.error.noFields'));
      return false;
    }

    const selectedFields = exportFields.filter(f => f.checked);

    // Préparer les données pour l'export
    const dataToExport = contactsToExport.map(contact => {
      const row: Record<string, string> = {};

      selectedFields.forEach(field => {
        if (field.field === 'created_at_formatted') {
          row[field.label] = formatDateTime(contact.created_at);
        } else {
          row[field.label] = contact[field.field]?.toString() || '';
        }
      });

      return row;
    });

    // Créer et télécharger le fichier Excel
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Contacts');

    const fileName = `linko_contacts_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    toast.success(
      t('listContacts:export.success', { count: contactsToExport.length }),
    );
    // Ne pas vider la liste des contacts après exportation
    return true;
  };

  // Nettoyer l'état
  const resetState = () => {
    setContactToDelete(null);
    setContactsToExport([]);
  };

  return {
    contactToDelete,
    setContactToDelete,
    contactsToExport,
    exportFields,
    handleDeleteContact,
    handleBulkDelete,
    prepareForExport,
    handleExportFieldChange,
    exportContacts,
    resetState,
  };
};
